/**
 * WHAT MATTERS 2026: the women's conference page, the microsite
 * (microsites/womens-conference) ported into the event widget. See
 * docs/event-experiences.md for the rule this follows and the plan.
 *
 * Ported so far: the hero (with its countdown, invite and venue sheet).
 * Following: schedule, slot picker, lunch, keynote, curtain, contact, prayer,
 * then the user menu, recap and teaser.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ExperienceProps } from '../types';
import type { SheetDress } from '@/lib/types';
import { Hero } from './components/Hero';
import { Schedule } from './components/Schedule';
import { KeynoteCard } from './components/KeynoteCard';
import { PrayerCard } from './components/PrayerCard';
import { ContactCard, hostNoteAction } from './components/ContactCard';
import { WelcomeCurtain } from './components/WelcomeCurtain';
import { FloatingTicket } from './components/FloatingTicket';
import { PlaceSheet } from './components/PlaceSheet';
import { MealsProvider } from './components/MealsProvider';
import { Swash, Vine } from './components/Botanical';
import { buildAgenda, buildMeals, buildSpeakers, buildTopics } from './lib/views';
import { COPY_DEFAULTS, type Copy } from './lib/copy';
import { facts, phaseOf } from './lib/facts';
import { art } from './lib/art';
import { fetchTransfers, offerPlace, withdrawPlaceOffer, type TransfersState } from '@/lib/api';
import { useSiteFooter } from '../kit/siteFooter';
import { CHROME } from './chrome';

/** Three faces, three jobs (theme.css, rule 2). Fonts in <head> reach the shadow DOM. */
const FONTS = [
  ['Cormorant+Garamond', 'ital,wght@0,400;0,500;0,600;1,400;1,500;1,600'],
  ['Figtree', 'wght@400;500;600;700'],
  ['Pinyon+Script', ''],
] as const;

function useFonts() {
  useEffect(() => {
    for (const [family, axes] of FONTS) {
      const id = `xp-wm-font-${family}`;
      if (document.getElementById(id)) continue;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${family}${axes ? `:${axes}` : ''}&display=swap`;
      document.head.appendChild(link);
    }
  }, []);
}

/**
 * The art CSS reaches for (paper grain, bloom source), set on the widget's
 * host elements: custom properties inherit through a shadow boundary, so the
 * page and the overlay host's sheets both see where the bundle was served from.
 */
function useArtVars() {
  useEffect(() => {
    const vars = {
      '--art-paper': `url('${art('/art/paper.jpg')}')`,
      '--art-key': `url('${art('/art/what-matters.png')}')`,
    };
    const hosts = [
      document.getElementById('event-details-overlay'),
      ...Array.from(document.querySelectorAll<HTMLElement>('#event-details-root, #eventDetails')),
    ].filter((el): el is HTMLElement => !!el);
    for (const el of hosts) for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
  }, []);
}

export default function WhatMatters2026(props: ExperienceProps) {
  const {
    details,
    me,
    clock,
    choices,
    onRegister,
    shareUrl,
    invoiceSlot,
    saveChoice,
    setRegistrationDress,
  } = props;
  useFonts();
  useArtVars();

  const config = useMemo(() => facts(details), [details]);
  const rows = details.page?.agenda ?? [];
  const speakers = useMemo(() => buildSpeakers(config, details), [config, details]);
  const topics = useMemo(() => buildTopics(config, details, speakers), [config, details, speakers]);
  const agenda = useMemo(() => buildAgenda(config, rows), [config, rows]);
  const meals = useMemo(() => buildMeals(config, rows), [config, rows]);
  // The shared registration sheet, in this event's clothes: each breakout a
  // speaker's face and the topic's own title, lunch as the caterer's photos.
  // MP's old group notes ("selections are final") are dropped: picks change
  // here until the lock date, and the sheet cannot make a duplicate.
  useEffect(() => {
    const options: NonNullable<SheetDress['options']> = {};
    for (const t of topics)
      for (const s of t.slots)
        options[s.optionPriceId] = {
          title: t.shortTitle,
          subtitle: [t.speaker.name, t.panel ? 'Panel host' : null].filter(Boolean).join(' · '),
          detail: t.room ?? undefined,
          imageUrl: t.speaker.imageSrc,
        };
    const groups: NonNullable<SheetDress['groups']> = {};
    config.breakoutGroups.forEach((g, i) => {
      groups[g.groupId] = {
        title: `${g.label} · ${g.agendaHint}`,
        hint:
          i === 0
            ? 'Every topic is taught in both sessions, so pick a different one for each.'
            : undefined,
        hideDescription: true,
      };
    });
    const answers: NonNullable<SheetDress['answers']> = {};
    if (meals.formFieldId) {
      answers[meals.formFieldId] = Object.fromEntries(
        meals.options.map((o) => [
          o.value,
          { label: o.label, detail: o.detail, tags: o.tags, photo: o.photo },
        ])
      );
    }
    // Form 2565's lunch heading is an 800-character note about Panera and
    // allergies; the photo cards and the allergy question say it.
    // HARDCODED: field 28126, the instructions row above the lunch question.
    const hideFields = rows.some((r) => r.question) ? [28126] : [];
    setRegistrationDress({ options, groups, answers, hideFields });
    return () => setRegistrationDress(null);
  }, [topics, meals, config, rows, setRegistrationDress]);

  // HARDCODED: the microsite's copy defaults. Its Neon/MP label layer is not
  // ported; dp_Application_Labels if staff need to edit (docs).
  const copy: Copy = COPY_DEFAULTS;
  const phase = phaseOf(clock.stage.stage);
  // A place is a seat that counts (02/03/04). An unpaid checkout comes back
  // with canChange false and is not a ticket: "Registered", the ticket and
  // "Your order" are for people who finished. Someone mid-payment is offered
  // the way to finish by the sign-up sheet (me.people[].already 'pending').
  const places = useMemo(() => (choices?.people ?? []).filter((p) => p.canChange), [choices]);
  const isRegistered = places.length > 0;
  const viewerContact = me?.viewer.contactId;
  // Whose picks the schedule shows: the viewer's own place first; the drawer
  // (or the chips over the schedule) can switch to anyone else in the house.
  const [shownId, setShownId] = useState<number | null>(null);
  const person =
    places.find((p) => p.eventParticipantId === shownId) ??
    places.find((p) => p.contactId === viewerContact) ??
    places[0] ??
    null;
  const scheduleRef = useRef<HTMLElement>(null);
  const [placeOpen, setPlaceOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  // Giving a place away (registration-transfers-1.0): only for a real
  // signed-in household, and only where the event allows it.
  const [transfers, setTransfers] = useState<TransfersState | null>(null);
  const realViewer = props.viewer === 'real' && !!me;
  useEffect(() => {
    if (!realViewer) return setTransfers(null);
    const c = new AbortController();
    fetchTransfers(props.apiBaseUrl, details.event.id, c.signal)
      .then((t) => !c.signal.aborted && setTransfers(t))
      .catch(() => {});
    return () => c.abort();
  }, [realViewer, props.apiBaseUrl, details.event.id]);
  const refreshTransfers = () =>
    fetchTransfers(props.apiBaseUrl, details.event.id)
      .then(setTransfers)
      .catch(() => {});
  useSiteFooter(pageRef, CHROME.footer);

  const faces = new Map((me?.people ?? []).map((p) => [p.contactId, p.image]));
  const ticketRef = useRef<HTMLButtonElement>(null);
  // An offer sent from the sheet pops the floating ticket's badge when the
  // sheet closes, so she sees where it went.
  const offeredInSheet = useRef(false);
  const [landKey, setLandKey] = useState(0);
  const offerOf = (eventParticipantId: number) =>
    transfers?.transfers.find(
      (x) =>
        x.eventParticipantId === eventParticipantId &&
        (x.status === 'offered' || x.status === 'approved')
    )?.status as 'offered' | 'approved' | undefined;
  // Tapping someone already registered in the sign-up sheet opens her place.
  useEffect(() => {
    const open = (e: Event) => {
      const contactId = (e as CustomEvent<{ contactId: number }>).detail?.contactId;
      const p = places.find((x) => x.contactId === contactId);
      if (!p) return;
      setShownId(p.eventParticipantId);
      setPlaceOpen(true);
    };
    window.addEventListener('event-details:open-place', open);
    return () => window.removeEventListener('event-details:open-place', open);
  }, [places]);
  const firstName = places[0]?.firstName ?? me?.people?.find((p) => p.isViewer)?.firstName ?? null;
  const spots = details.event.capacity
    ? { taken: details.event.registered, limit: details.event.capacity }
    : null;

  return (
    <div
      ref={pageRef}
      className="xp-wm bleed"
      style={
        {
          '--art-paper': `url('${art('/art/paper.jpg')}')`,
          '--art-key': `url('${art('/art/what-matters.png')}')`,
        } as React.CSSProperties
      }
    >
      {/* Greets her once per device and files her ticket into the site's
          login pill. Not on the day or after: she wants the schedule then. */}
      {person &&
      (props.viewer === 'real' || props.viewer === 'attendee') &&
      (phase === 'registration' || phase === 'preEvent') ? (
        <WelcomeCurtain
          name={`${person.firstName} ${person.lastName}`.trim()}
          firstName={person.firstName}
          imageUrl={faces.get(person.contactId) ?? null}
          eventId={details.event.id}
          ministryMark={config.ministryMark}
          always={props.viewer === 'attendee'}
          target={() => ticketRef.current}
        />
      ) : null}
      <Hero
        config={config}
        now={clock.now}
        shareUrl={shareUrl}
        onRegister={onRegister}
        phase={phase}
        capacity={spots}
        isRegistered={isRegistered}
        copy={copy}
        firstName={firstName}
        recap={
          config.recap
            ? // The microsite read the thanks from Neon (gathering_content) and
              // showed nothing until the team wrote it; content.ts holds a
              // placeholder describing a weekend nobody has lived yet.
              // HARDCODED: write the real words into content.ts afterward.
              { thanks: null, next: config.recap.next ?? null }
            : null
        }
      />
      {/* Ported from the microsite's page.tsx: the keynote, the invitation,
          then the schedule. */}
      <main className="xp-wm-main pb-16">
        {/* One element laps the hero: the keynote until the weekend is over.
            The recap's lead takes its place then (not ported yet). */}
        {phase !== 'recap' && phase !== 'teaser' ? (
          <KeynoteCard overlap config={config} agenda={agenda} />
        ) : null}

        {phase === 'registration' || phase === 'preEvent' ? (
          <section className="wm-shell relative mt-28 pb-4 text-center sm:mt-32 sm:pb-8">
            <div aria-hidden className="absolute -top-72 left-1/2 -z-10 -translate-x-1/2">
              <div className="wm-bloom wm-bloom-blush opacity-25" />
            </div>
            <p className="wm-kicker">
              {config.ministryMark}&rsquo;s Conference {config.eventStarts.slice(0, 4)}
            </p>
            <p className="wm-display text-wm-sage-950 mx-auto mt-3 max-w-3xl text-3xl italic sm:text-4xl">
              {config.invitation}
            </p>
            <span aria-hidden className="text-wm-sage-300 mt-5 flex justify-center">
              <Swash />
            </span>
            <p className="text-wm-sage-700 mx-auto mt-6 max-w-2xl text-lg">{config.description}</p>
          </section>
        ) : null}

        {invoiceSlot ? <section className="wm-shell mt-12">{invoiceSlot}</section> : null}

        {phase !== 'recap' && phase !== 'teaser' ? (
          <section
            id="schedule"
            ref={scheduleRef}
            className="wm-shell relative mt-16 scroll-mt-6 sm:mt-20"
          >
            <div aria-hidden className="absolute -top-40 -right-96 -z-10">
              <div className="wm-bloom wm-bloom-plum opacity-25" />
            </div>
            <Vine className="text-wm-sage-300/70 absolute top-24 -left-16 hidden h-[26rem] w-16 xl:block" />
            <Vine
              flip
              className="text-wm-sage-300/70 absolute top-[52rem] -right-16 hidden h-[26rem] w-16 xl:block"
            />
            {/* Not in the microsite, which kept the household in its profile
                sheet: with more than one place on the account, whose picks
                the rows show is a choice right here. */}
            {places.length > 1 ? (
              <div className="mb-8 flex flex-wrap gap-2">
                {places.map((p) => {
                  const on = p.eventParticipantId === person?.eventParticipantId;
                  return (
                    <button
                      key={p.eventParticipantId}
                      type="button"
                      onClick={() => setShownId(p.eventParticipantId)}
                      aria-pressed={on}
                      className={`wm-lift rounded-full px-4 py-2 text-sm font-semibold ${
                        on ? 'bg-wm-sage-800 text-wm-cream' : 'wm-glass text-wm-sage-700'
                      }`}
                    >
                      {p.firstName}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <MealsProvider meals={meals}>
              <Schedule
                config={config}
                topics={topics}
                speakers={speakers}
                person={person}
                locked={!!choices?.locked || clock.stage.choicesLocked}
                saveChoice={saveChoice}
                onRegister={onRegister}
                isRegistered={isRegistered}
                phase={phase}
                capacity={spots}
                nowIso={phase === 'live' ? clock.now : null}
                agenda={agenda}
              />
            </MealsProvider>
          </section>
        ) : null}
        {/* Prayer first, as on the microsite: one is the reason many of them
            came, the other is admin. Stacked on a phone; side by side once
            there is room, because a full-width strip left each card's arrow
            a screen away from its words. */}
        <section className="wm-shell mt-16 grid scroll-mt-6 grid-cols-1 gap-6 lg:grid-cols-2">
          <PrayerCard
            signedIn={!!me}
            copy={copy}
            apiBaseUrl={props.apiBaseUrl}
            eventId={details.event.id}
            pretend={clock.pretend}
          />
          <ContactCard
            // The note goes to the event's primary contact in MP (the API
            // route decides; a public form cannot name its recipient), so the
            // card names that person. The microsite hard-coded Misty Legler
            // because MP disagreed; set her as the event's contact to match.
            host={{
              name: details.event.primaryContactName ?? config.host.name,
              role: config.host.role,
              imageUrl: details.event.primaryContactImage,
            }}
            household={(me?.people ?? []).map((p) => ({
              contactId: p.contactId,
              name: `${p.firstName} ${p.lastName}`.trim(),
              firstName: p.firstName,
              email: p.email,
              imageUrl: p.image,
            }))}
            action={hostNoteAction(props.apiBaseUrl, details.event.id, clock.pretend)}
            copy={copy}
          />
        </section>
      </main>

      {/* Her ticket, docked bottom-right: the way into her place. Real
          viewers with a place, and the time machine's demo attendee. */}
      {(props.viewer === 'real' || props.viewer === 'attendee') && places.length ? (
        <FloatingTicket
          ref={ticketRef}
          label="Your ticket"
          people={places.map((p) => ({
            name: `${p.firstName} ${p.lastName}`.trim(),
            imageUrl: faces.get(p.contactId) ?? null,
            offer: offerOf(p.eventParticipantId) ?? null,
          }))}
          landKey={landKey}
          onOpen={() => setPlaceOpen(true)}
        />
      ) : null}

      {/* Her place, opened from the floating ticket or the sign-up sheet. */}
      <MealsProvider meals={meals}>
        <PlaceSheet
          open={placeOpen}
          onClose={() => {
            setPlaceOpen(false);
            if (offeredInSheet.current) {
              offeredInSheet.current = false;
              setLandKey((k) => k + 1);
            }
          }}
          people={places.map((p) => ({
            eventParticipantId: p.eventParticipantId,
            firstName: p.firstName,
          }))}
          onPerson={setShownId}
          config={config}
          person={person}
          imageUrl={person ? (faces.get(person.contactId) ?? null) : null}
          invoice={
            (person &&
              me?.invoices.find((inv) =>
                inv.people.some((p) => p.eventParticipantId === person.eventParticipantId)
              )) ||
            null
          }
          faces={faces}
          topics={topics}
          phase={phase}
          capacity={spots}
          locked={!!choices?.locked || clock.stage.choicesLocked}
          choicesCloseAt={details.page?.choicesCloseAt ?? null}
          saveChoice={saveChoice}
          onRegister={onRegister}
          signUpLabel={copy['profile.signUpSomeoneElse']}
          transfers={transfers}
          household={(me?.people ?? [])
            .filter((p) => !choices?.people.some((c) => c.contactId === p.contactId))
            .map((p) => ({
              contactId: p.contactId,
              name: `${p.firstName} ${p.lastName}`.trim(),
              imageUrl: p.image,
            }))}
          onOffer={async (offer) => {
            if (clock.pretend) return 'Not saved: you are in the time machine.';
            try {
              await offerPlace(props.apiBaseUrl, details.event.id, offer);
              await refreshTransfers();
              offeredInSheet.current = true;
              return null;
            } catch (err) {
              return err instanceof Error ? err.message : 'That did not save. Try again.';
            }
          }}
          onWithdraw={async (id) => {
            if (clock.pretend) return 'Not saved: you are in the time machine.';
            try {
              await withdrawPlaceOffer(props.apiBaseUrl, details.event.id, id);
              await refreshTransfers();
              return null;
            } catch (err) {
              return err instanceof Error ? err.message : 'That did not save. Try again.';
            }
          }}
        />
      </MealsProvider>
    </div>
  );
}
